import React, { ChangeEvent, FC, useContext, useRef } from "react";

import { AlertProps, Button, styled, SvgIcon, Tooltip } from "@mui/material";

import { ConfigContext } from "../../App";
import { ReactComponent as Upload } from "../../assets/icons/upload.svg";

import {
  FluidType,
  generalSettingsType,
  operatingPointType,
  UnitIdValueType,
} from "./ValvePilot";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useLogMatomoEvent } from "../../tools/logMatomoEvent";

const Input = styled("input")({
  display: "none",
});

interface ValvePilotPDFUploadButtonProps {
  setGeneralSettings: (generalSettings: generalSettingsType) => void;
  operatingPoints?: operatingPointType[];
  setOperatingPoints: (operatingPoints: operatingPointType[]) => void;
  setColumnsCount: (columnsCount: number) => void;
  setCalculationAlerts: (calculationAlerts: AlertProps[]) => void;
  setIsMediumSteam: (isMediumSteam: boolean) => void;
  setIsMediumFluid: (isMediumFlouid: boolean) => void;
  openDialog: (
    title: string,
    contentText: string,
    callback: () => void
  ) => void;
  fluids?: FluidType[];
  clearAll: () => void;
  showIconButtons: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

export interface generalSettingsTypeData {
  FluidName?: string;
  FluidId?: number;
  TagNo?: string;
  ValveType?: string;
  useVelocityLimit?: boolean;
  InletLimit?: UnitIdValueType;
  OutletLimit?: UnitIdValueType;
}

const ValvePilotPDFUploadButton: FC<ValvePilotPDFUploadButtonProps> = ({
  setGeneralSettings,
  operatingPoints,
  setOperatingPoints,
  setColumnsCount,
  setCalculationAlerts,
  setIsMediumFluid,
  setIsMediumSteam,
  clearAll,
  fluids,
  openDialog,
  showIconButtons,
  setIsLoading,
}) => {
  const { t } = useTranslation();
  const refHiddenInput = useRef<HTMLInputElement>(null);
  const config = useContext(ConfigContext);
  const correlationId = sessionStorage.getItem("correlationId");
  const logEvent = useLogMatomoEvent();

  const onOpenFile = () => {
    refHiddenInput.current!.value = "";
    refHiddenInput.current!.click();
  };

  const fixGeneralSettingsData = (generalSettings: generalSettingsTypeData) => {
    return {
      ...generalSettings,
      tag: generalSettings.TagNo,
      TagNo: undefined,
      InletLimit: undefined,
      OutletLimit: undefined,
      InletVelocityLimit: generalSettings.useVelocityLimit
        ? generalSettings.InletLimit
        : undefined,
      InletPipeLimit: !generalSettings.useVelocityLimit
        ? generalSettings.InletLimit
        : undefined,
      OutletVelocityLimit: generalSettings.useVelocityLimit
        ? generalSettings.OutletLimit
        : undefined,
      OutletPipeLimit: !generalSettings.useVelocityLimit
        ? generalSettings.OutletLimit
        : undefined,
    };
  };

  const handleUpload = async (pdfFile: File) => {
    if (pdfFile) {
      const formData = new FormData();
      formData.append("file", pdfFile);
      setIsLoading(true);

      try {
        clearAll();
        const response = await axios.post(
          config?.proxyUrl +
            `/ValveCalc/api/v1/Calculation/InputData/retrieveFromPDF?correlationId=${correlationId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (response) {
          const generalSettingsData = fixGeneralSettingsData(
            response.data.UebergreifendeNutzereingaben
          );

          setGeneralSettings(generalSettingsData);
          if (fluids) {
            const fluid = fluids.find(
              (f: FluidType) =>
                f.ID ===
                Number(response.data.UebergreifendeNutzereingaben.FluidId)
            );
            if (fluid) {
              setIsMediumFluid(fluid.isMediumFluid);
              setIsMediumSteam(fluid.isMediumSteam);
            }
          }

          let operatingPointsData = operatingPoints as operatingPointType[];

          const operatingPointsCount = response?.data?.Betriebspunkte.length;

          if (operatingPointsCount <= 6) {
            setColumnsCount(operatingPointsCount);

            let operatingPoints = response?.data?.Betriebspunkte;
            for (let i = 0; i < operatingPointsCount; i++) {
              operatingPointsData[i] = {
                ...operatingPoints?.[i],
                AdditionalInformation:
                  response.data.Betriebspunkte[i].ZusatzInfo,
              } as operatingPointType;

              operatingPoints[i].AdditionalInformation =
                response.data.Betriebspunkte[i].ZusatzInfo;
            }
            setOperatingPoints(operatingPointsData);

            logEvent("ValvePilot Online", "saveDatasheet", "retrieveFromPDF");
          } else {
            setCalculationAlerts([
              {
                severity: "error",
                children: t("Alert.ERROR.InvalidPDF"),
              },
            ]);
            clearAll();
            console.error(t("Alert.ERROR.InvalidPDF"), operatingPointsCount);
          }
        }
      } catch (error) {
        console.error(t("Alert.ERROR.Load_NoData"), error);
      }
      setIsLoading(false);
    }
  };

  return (
    <>
      <Input
        type="file"
        accept="application/pdf"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          e.target.files?.[0] ? handleUpload(e.target.files[0]) : undefined
        }
        ref={refHiddenInput}
      />
      <Tooltip title={t("Alert.INFO.LOAD")} placement="top" arrow>
        <Button
          variant="contained"
          onClick={() =>
            openDialog(
              t("Dialog.UPLOAD_FILE.TITLE"),
              t("Dialog.UPLOAD_FILE.CONTENT_TEXT"),
              onOpenFile
            )
          }
          startIcon={
            showIconButtons ? (
              <SvgIcon component={Upload} inheritViewBox />
            ) : undefined
          }
        >
          {t("Buttons.LOAD_DATASHEET")}
        </Button>
      </Tooltip>
    </>
  );
};
export default ValvePilotPDFUploadButton;
